import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getNodeTriplet,
  $getTableCellNodeFromLexicalNode,
  $getTableColumnIndexFromTableCellNode,
  $isTableCellNode,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table";
import { mergeRegister } from "@lexical/utils";
import { $getNodeByKey, $getSelection, $isRangeSelection, COMMAND_PRIORITY_EDITOR } from "lexical";
import { type FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { $isDataTableCellNode, type DataTableCellNode } from "../nodes/data-table-cell-node";
import { StylableTableCellMui } from "../nodes/stylable-table-cell-mui";
import { $isStylableTableCellNode } from "../nodes/stylable-table-cell-node";
import { SET_TABLE_CELL_BACKGROUND_COLOUR_COMMAND } from "../types/commands";
import {
  getTableColumnWidths,
  sumLeftPercentagesFromWidths,
} from "../utils/get-table-column-widths";
import { useStyleableColumns } from "../utils/use-styleable-columns";
import { useColumnResizer } from "./table-menu/hooks/use-column-resizer";
import { useGetElementRect } from "./table-menu/hooks/use-get-element-rect";
import { useShowGridLines } from "./table-menu/hooks/use-show-grid-lines";
import { ResizeHandles } from "./table-menu/resize-handles-display";
import { TableColumnPopoverMenuButton } from "./table-menu/table-column-popover-menu-button";

type HandleStaticProps = {
  pillarIndex: number;
};
type HandleDynamicProps = {
  leftPercent: number;
  isCurrentlyResizing: boolean;
};
export type BoundaryConditions = {
  tableLeft: number;
  tableWidth: number;
  handles: HandleStaticProps[];
  selectedColumnIndex: number;
};

export type DynamicConditions = {
  handles: HandleDynamicProps[];
};

// anchorElem is the editor itself.
export const TableMenuPlugin: FC<{
  anchorElem: HTMLElement;
}> = ({ anchorElem }) => {
  const [editor] = useLexicalComposerContext();
  const stylableColumns = useStyleableColumns();
  useShowGridLines();
  const getElementRect = useGetElementRect();
  const [nodeState, setNodeState] = useState<{
    tableCellNode: TableCellNode | StylableTableCellMui | DataTableCellNode;
    tableRowNode: TableRowNode;
    tableNode: TableNode;
  } | null>(null);

  const [boundaryConditions, setBoundaryConditions] = useState<BoundaryConditions | null>(null);
  const [dynamicConditions, setDynamicConditions] = useState<DynamicConditions | null>(null);

  const menuButtonRef = useRef(null);
  const tableRowOVerlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!nodeState) {
      stylableColumns.enabled && setBoundaryConditions(null);
    }
  }, [nodeState, stylableColumns]);

  const handleColumnWidthsChange = useCallback(
    (newWidths: number[], movingPillarIndex: number, movingHandleIndex: number) => {
      if (nodeState) {
        // calculate the leftPercent of the handle being dragged
        const handles = dynamicConditions?.handles || [];
        // Add up the widths of the columns to the left of the moving handle
        const leftPercent = sumLeftPercentagesFromWidths(newWidths, movingPillarIndex);
        // eslint-disable-next-line react-hooks/immutability
        handles[movingHandleIndex]!.leftPercent = leftPercent;
        setDynamicConditions({ handles });
        editor.update(() => {
          nodeState.tableNode.setColWidths(newWidths);
        });
      }
    },
    [editor, nodeState, dynamicConditions],
  );

  const handleResizeEnd = useCallback(() => {
    if (nodeState) {
      // this is a hack to force the existing menu button to re-render and move to the new position
      setNodeState({ ...nodeState });
    }
  }, [nodeState]);

  const { currentlyInteractingPillarIndex, handlePointerDown } = useColumnResizer({
    handleColumnWidthsChange,
    handleResizeEnd,
    boundaryConditions,
  });

  // Detect which (if any ) table cell is selected and get a reference to it.
  const assignSelection = useCallback(() => {
    const menu = menuButtonRef.current;
    const selection = $getSelection();
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (selection === null || menu === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      $isRangeSelection(selection) &&
      rootElement !== null &&
      nativeSelection !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      // this could have been replaced with a stylable table cell node
      const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(
        selection.anchor.getNode(),
      );

      if (
        !$isTableCellNode(tableCellNodeFromSelection) &&
        !$isStylableTableCellNode(tableCellNodeFromSelection)
      ) {
        setNodeState(null);
        stylableColumns.enabled && setBoundaryConditions(null);
        return;
      }

      const tableCellParentNodeDOM = editor.getElementByKey(tableCellNodeFromSelection.getKey());

      if (tableCellParentNodeDOM === null) {
        setNodeState(null);
        stylableColumns.enabled && setBoundaryConditions(null);
        return;
      }

      const [tableCellNode, tableRowNode, tableNode] = $getNodeTriplet(tableCellNodeFromSelection);

      setNodeState({
        tableCellNode,
        tableRowNode,
        tableNode,
      });

      if (!stylableColumns.enabled) {
        return;
      }

      const columnWidths = getTableColumnWidths(tableNode);

      const tableElementRect = getElementRect(tableNode);
      const anchorRectRect = anchorElem.getBoundingClientRect();

      const selectedColumnIndex = $getTableColumnIndexFromTableCellNode(tableCellNodeFromSelection);

      // The 0th pillar is between the left hand column and the 1st column
      // The last pillar is between the second right column and the most right hand column
      // The left hand and right hand columns can only resize the inside columns
      // Only the currentColumnIndex has handles on either side of it.

      const handlesStatic: HandleStaticProps[] = [];
      const handlesDynamic: HandleDynamicProps[] = [];
      //left handle
      if (selectedColumnIndex > 0) {
        handlesStatic.push({
          pillarIndex: selectedColumnIndex - 1,
        });
        handlesDynamic.push({
          leftPercent: columnWidths.slice(0, selectedColumnIndex).reduce((acc, curr) => acc + curr),
          isCurrentlyResizing: false,
        });
      }
      //right handle
      if (selectedColumnIndex < columnWidths.length - 1) {
        handlesStatic.push({
          pillarIndex: selectedColumnIndex,
        });
        handlesDynamic.push({
          leftPercent: columnWidths
            .slice(0, selectedColumnIndex + 1)
            .reduce((acc, curr) => acc + curr),
          isCurrentlyResizing: false,
        });
      }

      if (columnWidths && tableElementRect && anchorRectRect) {
        setBoundaryConditions({
          tableLeft: tableElementRect.left + anchorRectRect.left,
          tableWidth: tableElementRect.width,
          selectedColumnIndex,
          handles: handlesStatic,
        });
        setDynamicConditions({
          handles: handlesDynamic,
        });
      }
    } else if (!activeElement) {
      setNodeState(null);
      stylableColumns.enabled && setBoundaryConditions(null);
    }
  }, [editor, anchorElem, stylableColumns, getElementRect]);

  // Only listen for updates when it's stationary otherwise we get a loop
  useEffect(() => {
    if (currentlyInteractingPillarIndex === null) {
      return mergeRegister(
        editor.registerUpdateListener(() => {
          editor.getEditorState().read(() => {
            assignSelection();
          });
        }),
        editor.registerCommand(
          SET_TABLE_CELL_BACKGROUND_COLOUR_COMMAND,
          ({ nodeKey, colour, skipHistory }) => {
            editor.update(
              () => {
                const tableCellNode = $getNodeByKey(nodeKey);
                if (!$isTableCellNode(tableCellNode)) {
                  return false;
                }
                tableCellNode.setBackgroundColor(colour);
              },
              skipHistory ? { tag: "historic" } : {},
            );
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
      );
    }
  }, [currentlyInteractingPillarIndex, assignSelection, editor]);

  useLayoutEffect(() => {
    const menuButtonDOM = menuButtonRef.current as HTMLButtonElement | null;

    if (menuButtonDOM !== null && nodeState) {
      const tableCellRect = getElementRect(nodeState.tableCellNode);

      if (tableCellRect) {
        const anchorRect = anchorElem.getBoundingClientRect();
        const menuRect = menuButtonDOM.getBoundingClientRect();

        // Make the button at the top of the cell like the floating toolbar
        const top =
          tableCellRect.top -
          anchorRect.top -
          ($isDataTableCellNode(nodeState.tableCellNode) ? 0 : menuRect.height);
        const left = tableCellRect.right - menuRect.width - anchorRect.left - 2;

        menuButtonDOM.style.opacity = "1";
        menuButtonDOM.style.transform = `translate(${left}px, ${top}px)`;
      } else {
        menuButtonDOM.style.opacity = "0";
        menuButtonDOM.style.transform = "translate(-10000px, -10000px)";
      }
    }
  }, [menuButtonRef, nodeState, editor, anchorElem, getElementRect]);

  useLayoutEffect(() => {
    // this will block the handles appearing and therefore disable the rest.
    if (!stylableColumns.enabled) {
      return;
    }
    const tableRowOverlayDOM = tableRowOVerlayRef.current;

    if (!tableRowOverlayDOM) {
      return;
    }

    if (nodeState) {
      const tableRowRect = getElementRect(nodeState.tableRowNode);
      if (tableRowRect) {
        const anchorRect = anchorElem.getBoundingClientRect();
        // The table row overlay is the invisible box that sits over the table to position the column resize handles
        const tableRowOverlayTop = tableRowRect.top - anchorRect.top;
        const tableRowOverlayLeft = tableRowRect.left - anchorRect.left;
        tableRowOverlayDOM.style.left = `${tableRowOverlayLeft}px`;
        tableRowOverlayDOM.style.top = `${tableRowOverlayTop}px`;
        tableRowOverlayDOM.style.width = `${tableRowRect.width}px`;
        tableRowOverlayDOM.style.height = `${tableRowRect.height}px`;
        tableRowOverlayDOM.style.display = "block";
      }
    } else {
      tableRowOverlayDOM.style.display = "none";
    }
  }, [tableRowOVerlayRef, nodeState, editor, anchorElem, stylableColumns, getElementRect]);

  return createPortal(
    <>
      <div ref={tableRowOVerlayRef} className="lexical-table-row-overlay">
        {stylableColumns.enabled &&
          boundaryConditions &&
          dynamicConditions &&
          nodeState &&
          !$isDataTableCellNode(nodeState?.tableCellNode) && (
            <ResizeHandles
              boundaryConditions={boundaryConditions}
              tableNode={nodeState.tableNode}
              dynamicConditions={dynamicConditions}
              handlePointerDown={handlePointerDown}
              currentlyInteractingPillarIndex={currentlyInteractingPillarIndex}
            />
          )}
      </div>
      <div className="table-cell-menu-button" ref={menuButtonRef}>
        {nodeState && <TableColumnPopoverMenuButton tableCellNode={nodeState.tableCellNode} />}
      </div>
    </>,
    anchorElem,
  );
};
